// The Terminal projection: one audit row in, one rendered line or nothing out
// (#4435, epic #4391).
//
// ─── WHAT THE TABLE BUYS ─────────────────────────────────────────────────────
//
// `TERMINAL_RENDERERS` is a `Record<AuditEventKind, …>` over the WHOLE closed
// vocabulary — 190 kinds (2026-09-19, #4400 added `blocker.asked`/`.answered`/
// `runner.online`/`inbound.received`) — and that total-ness is the entire point of this
// slice. `AuditEventKind` is `z.infer<typeof z.enum(AuditEventKind)>` over a
// const object (`../audit/kind.ts:24-32`), so it is a genuine literal union and
// a kind with no entry here is a compile error, not a blank line on a console
// nobody thought to check. Adding an audit kind therefore costs a decision about
// how it reads — which is the cost we want it to have.
//
// `'omit'` is that decision's second answer. It is not an absence: a missing key
// fails typecheck, an omitted one is a person having looked at the kind and said
// no, in a diff a reviewer can read.
//
// ─── THE ONE PLACE THE UNION MEETS A STRING ──────────────────────────────────
//
// `TerminalRow.kind` is the raw `audit_events.kind` column and is typed `string`.
// Writing `TERMINAL_RENDERERS[row.kind as AuditEventKind]` would compile, would
// stay exhaustive at the type level, and would be bypassed at RUNTIME — an
// unknown kind indexing to `undefined` and crashing on `.level`. So the kind is
// PARSED (`AuditEventKindSchema.safeParse`) and a miss becomes the `| null` in
// this function's signature. That is what the nullable return is for: a row whose
// kind this build does not know is a row from a newer writer, and "nothing" is
// the honest rendering of it.
//
// ─── THE FIVE GROUPS ARE A FILE SPLIT, AND A PARTITION ───────────────────────
//
// Mirrors `../audit/kind.ts` exactly, including the hazard. Composing by spread
// means a kind rendered in two groups is SILENTLY overwritten by the later one —
// where the single object literal this replaces would have been a `ts(1117)`
// duplicate-key error. The split is a file split so five agents can work without
// colliding, never a semantic one: a kind belongs to whichever group its
// neighbours are in. `TERMINAL_RENDERER_GROUPS` below exists so a partition test
// can walk the five individually and prove no key appears twice, the way
// `audit.test.ts` does for the vocabulary itself.
//
// ─── `run_heartbeat`, AND WHY IT IS DOUBLY UNREACHABLE HERE ──────────────────
//
// #4435 step 4 asks that `run_heartbeat` never appear in any output. It cannot,
// and the reason is stronger than a rule — it is not addressable from this row
// at all. Measured 2026-09-18:
//
//   1. `run_heartbeat` is not an `AuditEventKind`. It is the `event` value inside
//      a `task_run_events` row's PAYLOAD (`RUN_HEARTBEAT_EVENT`,
//      `../../value-objects/task-run-event/row.ts:65`).
//   2. The audit row attesting that ingest is `task.run_event`
//      (`AuditEventKind.TaskRunEventIngested`), and its writer
//      (`apps/worker/src/fleet/event-sink.ts:339`) records
//      `{ runId, taskId, kind, eventId }` — where `kind` is the run-event kind
//      (`status`, `tool_call`), never the sub-kind. The heartbeat discriminator
//      is not in the audit payload in the first place.
//   3. That writer sets `visibleToMaintainer: false`, and the terminal query
//      withholds `input`/`output` on exactly those rows via a `case when` in the
//      SELECT. So this projection sees `input: null` on every `task.run_event`
//      row regardless.
//
// The consequence: the table's entry for `task.run_event` is AUTHORITATIVE and
// there is no second guard here. A pre-table refusal would be the same invariant
// stated twice, needing its own reconciliation test, against the one-seam rule —
// and #4435 already scopes the run-event sub-kind table out of this slice ("the
// verbose slice, not here"), which makes `'omit'` the asked-for answer rather
// than a workaround. The maintenance family owns that entry; a test pinning it to
// `'omit'` is the guarantee.

import type {
  TerminalAttempt,
  TerminalEvent,
  TerminalRenderer,
  TerminalRendererEntry,
  TerminalRendererGroup,
  TerminalRow,
} from '../../wire/terminal';
import { type AuditEventKind, AuditEventKindSchema } from '../audit';
import { TERMINAL_RENDERERS_COMMERCE } from './renderers/commerce';
import { TERMINAL_RENDERERS_DISPATCH } from './renderers/dispatch';
import { TERMINAL_RENDERERS_LOOP } from './renderers/loop';
import { TERMINAL_RENDERERS_MAINTENANCE } from './renderers/maintenance';
import { TERMINAL_RENDERERS_REVIEW } from './renderers/review';

/**
 * The five family tables, named, in composition order.
 *
 * Exported for the partition test and for nothing else — a caller wanting a
 * kind's renderer wants {@link TERMINAL_RENDERERS}, which is the composed whole.
 * The order here IS the spread order below, so a partition failure names the
 * group that would have won.
 */
export const TERMINAL_RENDERER_GROUPS: readonly {
  readonly name: string;
  readonly group: TerminalRendererGroup;
}[] = [
  { name: 'loop', group: TERMINAL_RENDERERS_LOOP },
  { name: 'dispatch', group: TERMINAL_RENDERERS_DISPATCH },
  { name: 'maintenance', group: TERMINAL_RENDERERS_MAINTENANCE },
  { name: 'commerce', group: TERMINAL_RENDERERS_COMMERCE },
  { name: 'review', group: TERMINAL_RENDERERS_REVIEW },
];

/**
 * Every audit kind's Terminal decision. TOTAL over `AuditEventKind` — the
 * annotation is what makes an unmapped kind a compile error, so it is not
 * decoration and must not be relaxed to `Partial` or to a `string` key.
 */
export const TERMINAL_RENDERERS: Record<AuditEventKind, TerminalRendererEntry> = {
  ...TERMINAL_RENDERERS_LOOP,
  ...TERMINAL_RENDERERS_DISPATCH,
  ...TERMINAL_RENDERERS_MAINTENANCE,
  ...TERMINAL_RENDERERS_COMMERCE,
  ...TERMINAL_RENDERERS_REVIEW,
};

/**
 * Where a failure sits on the retry ladder, read off the row.
 *
 * Exported so a test can pin each branch without going through a renderer. The
 * three outcomes and the writers behind them are enumerated on `TerminalAttempt`
 * (`../../wire/terminal.ts`); the short version is that `output.attempt` is the
 * only recorded number and there is no ceiling on this row to pair it with, so
 * `nth` is what a real `task.failed` row produces today.
 */
export function resolveTerminalAttempt(row: TerminalRow): TerminalAttempt {
  // Not a guess about the payload's shape: on a row that is not
  // maintainer-visible the SELECT nulls `output` outright, so "absent" here has
  // two different causes and a renderer deserves to know which.
  if (!row.visibleToMaintainer) return { kind: 'unknown', reason: 'payload_withheld' };
  const n = attemptNumber(row.output);
  return n === null ? { kind: 'unknown', reason: 'not_recorded' } : { kind: 'nth', n };
}

/** `output.attempt` when it is a usable attempt number, else null. */
function attemptNumber(output: unknown): number | null {
  if (typeof output !== 'object' || output === null) return null;
  const v = (output as Record<string, unknown>)['attempt'];
  // `Number.isInteger` and a floor of 1: an attempt ladder starts at one, and a
  // 0, a float or a stringified number is a writer disagreeing with this reader
  // rather than a value to render. Rendering it would put a wrong number on a
  // failure line, which is worse than the honest `not_recorded`.
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 ? v : null;
}

/**
 * Project one audit row into a Terminal line.
 *
 * `null` for three reasons, all of them decisions rather than errors: the kind is
 * not in this build's vocabulary, the kind is mapped `'omit'`, or the renderer
 * looked at this particular row and had nothing to say.
 */
export function projectTerminalEvent(row: TerminalRow): TerminalEvent | null {
  const kind = AuditEventKindSchema.safeParse(row.kind);
  if (!kind.success) return null;

  const entry = TERMINAL_RENDERERS[kind.data];
  if (entry === 'omit') return null;

  const rendered = renderEntry(entry, row);
  if (rendered === null) return null;

  return {
    id: row.id,
    ts: row.ts.toISOString(),
    // A `by_row` kind answers its level with the line (`task.moved`, #6768).
    level: rendered.level ?? (entry.level === 'by_row' ? 'warn' : entry.level),
    // The ONE override a kind cannot know about itself. `userId` is non-null
    // exactly when a person's credential drove the write, and on those rows the
    // kind's declared actor ("the babysitter does this") is wrong: a human did.
    // Every other refinement is the renderer's, because it is the kind's.
    actor: row.userId === null ? entry.actor : 'human',
    subject: rendered.subject,
    line: rendered.line,
    refs: rendered.refs,
    // Not a renderer's choice, deliberately: the session is the causal chain the
    // console groups by, and two lines of one session must agree about it. A
    // per-kind correlation id would let them disagree.
    correlation_id: row.sessionId,
    has_raw: row.visibleToMaintainer,
  };
}

/**
 * Dispatch on the renderer's declared level — the one place the `fail` arm's
 * extra input is supplied.
 *
 * Separated from {@link projectTerminalEvent} so the narrowing is readable: the
 * `fail` renderer takes a context the other four do not, and the `TerminalEvent`
 * assembly above has no business also resolving an attempt ladder.
 */
function renderEntry(
  entry: TerminalRenderer,
  row: TerminalRow,
): {
  line: TerminalEvent['line'];
  subject: TerminalEvent['subject'];
  refs: TerminalEvent['refs'];
  level?: TerminalEvent['level'];
} | null {
  if (entry.level === 'fail') {
    return entry.render({ row, attempt: resolveTerminalAttempt(row) });
  }
  return entry.render({ row });
}
